import React, { useEffect, useRef, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { RootState, storeActions } from '../../../store'

import { CurrentSubView, Mode, RemoteButtonType, RecognitionState, Theme, CloseButtonCause, Routes } from '../../../models/enums'
import * as RemoteIcons from '../../Icons/RemoteIcons'
import { KeyboardIcon } from '../../Icons/MenuIcons'
import { CloseIcon } from '../../Icons/Miscellaneous'

interface RemoteButtonProps {
  type: RemoteButtonType
  backgroundColor?: string
  cause?: CloseButtonCause
}

const RemoteButton = ({ type, backgroundColor, cause }: RemoteButtonProps): JSX.Element => {
  const dispatch = useDispatch()
  const translation = useSelector((state: RootState) => state.localeReducer.currentTranslations)
  const theme = useSelector((state: RootState) => state.viewReducer.theme)
  const displaySubtitles = useSelector((state: RootState) => state.retorikReducer.displaySubtitles)
  const muted = useSelector((state: RootState) => state.speechReducer.muted)
  const currentSubView = useSelector((state: RootState) => state.utilsReducer.currentSubView)
  const currentSubViewRef = useRef<CurrentSubView>(CurrentSubView.none)
  const [icon, setIcon] = useState<JSX.Element>()
  const [label, setLabel] = useState<string>()
  const [hover, setHover] = useState<boolean>(false)
  const [backgroundColorHover, setBackgroundColorHover] = useState<string | undefined>(undefined)

  useEffect(() => {
    currentSubViewRef.current = currentSubView
  }, [currentSubView])

  useEffect(() => {
    setBackgroundColorHover(backgroundColor?.replace('99', 'E6'))
  }, [backgroundColor])

  useEffect(() => {
    switch (type) {
      case RemoteButtonType.history:
        setLabel(translation.common.history)
        break
      case RemoteButtonType.home:
        setLabel('home')
        break
      case RemoteButtonType.language:
        setLabel(translation.common.lang)
        break
      case RemoteButtonType.menu:
        setLabel(translation.common.menu)
        break
      case RemoteButtonType.sound:
        setLabel(translation.common.sound)
        break
      case RemoteButtonType.subtitles:
        setLabel(translation.common.subtitle)
        break
      case RemoteButtonType.text:
        setLabel(translation.common.mode)
        break
      case RemoteButtonType.close:
        setLabel(translation.common.close)
        break
    }
  }, [type, translation])

  useEffect(() => {
    switch (type) {
      case RemoteButtonType.history:
        setIcon(<RemoteIcons.HistoryIcon color={theme === Theme.dark ? '#FFF' : '#000'} />)
        break
      case RemoteButtonType.home:
        setIcon(<RemoteIcons.HomeIcon color={theme === Theme.dark ? '#FFF' : '#000'} />)
        break
      case RemoteButtonType.language:
        setIcon(<RemoteIcons.LanguageIcon color={theme === Theme.dark ? '#FFF' : '#000'} />)
        break
      case RemoteButtonType.menu:
        setIcon(<RemoteIcons.MenuIcon color={theme === Theme.dark ? '#FFF' : '#000'} />)
        break
      case RemoteButtonType.sound:
        setIcon(muted ? <RemoteIcons.SoundOffIcon color={theme === Theme.dark ? '#FFF' : '#000'} /> : <RemoteIcons.SoundOnIcon color={theme === Theme.dark ? '#FFF' : '#000'} />)
        break
      case RemoteButtonType.subtitles:
        setIcon(displaySubtitles ? <RemoteIcons.SubtitlesOnIcon color={theme === Theme.dark ? '#FFF' : '#000'} /> : <RemoteIcons.SubtitlesOffIcon color={theme === Theme.dark ? '#FFF' : '#000'} />)
        break
      case RemoteButtonType.text:
        setIcon(<KeyboardIcon className='rf-w-5' color='#A4A7AF' />)
        break
      case RemoteButtonType.close:
        setIcon(<CloseIcon className='rf-h-8 rf-w-8' color='#000' strokeWidth={1} />)
        break
    }
  }, [type, theme, displaySubtitles, muted])

  const handleClick = (): void => {
    switch (type) {
      case RemoteButtonType.history:
        if (currentSubViewRef.current === CurrentSubView.history) {
          dispatch(storeActions.utils.setCurrentSubView(CurrentSubView.none))
          dispatch(storeActions.directline.sendEvent({ name: 'Davi.CloseWindow', callback: () => dispatch(storeActions.directline.sendEvent({ name: 'Davi.CloseWindowHistory' })) }))
        } else {
          dispatch(storeActions.utils.setCurrentSubView(CurrentSubView.history))
        }
        break
      case RemoteButtonType.home:
        dispatch(storeActions.utils.setCurrentSubView(CurrentSubView.none))
        dispatch(storeActions.directline.sendEvent({ name: 'showHome' }))
        break
      case RemoteButtonType.language:
        if (currentSubViewRef.current === CurrentSubView.languages) {
          dispatch(storeActions.utils.setCurrentSubView(CurrentSubView.none))
          dispatch(storeActions.directline.sendEvent({ name: 'Davi.CloseWindow', callback: () => dispatch(storeActions.directline.sendEvent({ name: 'Davi.CloseWindowLanguages' })) }))
        } else {
          dispatch(storeActions.utils.setCurrentSubView(CurrentSubView.languages))
        }
        break
      case RemoteButtonType.menu:
        dispatch(storeActions.utils.toggleDashboard())
        break
      case RemoteButtonType.sound:
        dispatch(storeActions.speech.toggleMuted())
        break
      case RemoteButtonType.subtitles:
        dispatch(storeActions.retorik.toggleSubtitles())
        break
      case RemoteButtonType.text:
        dispatch(storeActions.retorik.setCanFocusSendBox(true))
        dispatch(storeActions.speech.setActiveRecognitionState(RecognitionState.ForceClosing))
        dispatch(storeActions.speech.setMode(Mode.text))
        break
      case RemoteButtonType.close: {
        switch (cause) {
          case CloseButtonCause.dashboard:
            dispatch(storeActions.utils.setDashboardOpened(false))
            break
          case CloseButtonCause.detailedpoi:
            dispatch(storeActions.utils.setCloseDetailedPOI(true))
            break
          case CloseButtonCause.fullscreenimage:
            dispatch(storeActions.utils.setFullScreenImage(undefined))
            break
          case CloseButtonCause.list:
            dispatch(storeActions.utils.setCloseList(Date.now()))
            break
          case CloseButtonCause.emergency:
          case CloseButtonCause.history:
          case CloseButtonCause.languages:
          case CloseButtonCause.weather:
          case CloseButtonCause.map:
            dispatch(storeActions.utils.setCurrentSubView(CurrentSubView.none))
            dispatch(storeActions.directline.sendEvent({ name: 'Davi.CloseWindow', callback: () => dispatch(storeActions.directline.sendEvent({ name: `Davi.CloseWindow${cause}` })) }))
            break
          case CloseButtonCause.news:
            dispatch(storeActions.view.setRoute(Routes.Home))
            break
          case CloseButtonCause.none:
          default:
            break
        }
      }
    }
  }

  return (
    <div
      id={`retorik-framework-remote-${type}`}
      className='rf-w-10 rf-h-10 rf-rounded-half rf-flex rf-justify-center rf-items-center rf-cursor-pointer'
      style={{
        zIndex: type === RemoteButtonType.close ? 60 : 5,
        backgroundColor: hover ? backgroundColorHover : backgroundColor,
        boxShadow: type === RemoteButtonType.text || type === RemoteButtonType.close ? '0px 0px 6px #1A1A1A80' : undefined
      }}
      aria-label={label}
      // data-map used in interactive map to use this button externaly
      data-map={type === RemoteButtonType.close ? 'fermer' : 'other'}
      onClick={handleClick}
      onPointerEnter={(): void => setHover(true)}
      onPointerLeave={(): void => setHover(false)}
    >
      {icon}
    </div>
  )
}

export default RemoteButton
